/**
 * BL-2117 / BL-2131 — the request schema for `PATCH /api/lots/[id]`: the catalog
 * patch (`LotCatalogPatchSchema`) plus the lot money fields, which travel as
 * MoneyWire through the shared lot-money field and arrive decoded to `Money`.
 *
 * Composed here rather than inside `LotCatalogPatchSchema` so the catalog field
 * gates stay a money-free unit the schema manifest can list on their own; the
 * money field pulls in `decimal.js` and core's Money codec.
 */
import { fromWire, type MoneyWire } from '@auctogon/core/money';
import { decodeWireAmount } from '@auctogon/core/realtime/payloads/money-wire-amount';
import { MONEY_WIRE_MALFORMED_MESSAGE } from '@auctogon/core/schemas/money-wire';
import { z } from '@auctogon/core/zod';

import { LotCatalogPatchSchema } from './inventory';
import { LotDocumentChangesSchema, LotMediaChangesSchema } from './lot-attachments';
import { LotCategoryIdSchema } from './lot-category';
import { addAdditionalContactPhoneIssue, addOtherContactReachableIssue, LotContactSlotFields, type LotContactRuleOptions } from './lot-contacts';
import { lotMoneyWireField } from './lot-monetary-fields';

/**
 * BL-2336 — a money BASE value: a MoneyWire decoded to `Money`, structurally
 * only. A base is the server's own earlier value, so it is not held to today's
 * lot-money rules (sign, step, ceiling) — a lot priced before a rule existed
 * must still replay. `null` is an unset base ("no reserve").
 */
const moneyBaseField = z
  .custom<MoneyWire>((value) => decodeWireAmount(value).kind === 'money', MONEY_WIRE_MALFORMED_MESSAGE)
  .transform((wire) => fromWire(wire))
  .nullable()
  .optional();

/** A non-money base: compared as the write would store it, so any JSON value. */
const plainBaseField = z.unknown().optional();

/**
 * BL-2336 — `baseValues`: the value each field an offline edit changed had when
 * the edit began, keyed like the PATCH body. The gate compares only these with
 * the row it locks; a mismatch is a 409 `STALE_FIELDS`.
 *
 * A closed set (`.strict()`): the PATCH's editable fields, minus `media` and
 * `documents` (offline media and documents use their own per-item operations)
 * and minus `status` / `moveToAuctionId` (offline status goes through
 * `/api/lots/batch`, and a move is its own branch). The key order here is the
 * order the 409 lists stale fields in.
 */
export const LotPatchBaseValuesSchema = z
  .object({
    name: plainBaseField,
    description: plainBaseField,
    startPrice: moneyBaseField,
    reserve: moneyBaseField,
    increment: moneyBaseField,
    sellerId: plainBaseField,
    taxStatus: plainBaseField,
    taxRateId: plainBaseField,
    pricingType: plainBaseField,
    categoryId: plainBaseField,
    acreage: plainBaseField,
    isTitledItem: plainBaseField,
    titleState: plainBaseField,
    titleStatus: plainBaseField,
    titleMailingStatus: plainBaseField,
    titleMailingTrackingNumber: plainBaseField,
    titleMailingDateSent: plainBaseField,
    titleMailingRecipientName: plainBaseField,
    titleMailingRecipientAddress: plainBaseField,
    hasLoadOutFee: plainBaseField,
    loadOutFee: moneyBaseField,
    visibleToSeller: plainBaseField,
    customFields: plainBaseField,
    vin: plainBaseField,
    serialNumber: plainBaseField,
    make: plainBaseField,
    model: plainBaseField,
    locationId: plainBaseField,
    year: plainBaseField,
    hoursMileage: plainBaseField,
    mileageUnit: plainBaseField,
    salesRepId: plainBaseField,
    // BL-2748: the contact slots, compared as the write would store them.
    showSalesRep: plainBaseField,
    showSeller: plainBaseField,
    showAdditionalContact: plainBaseField,
    additionalContactTitle: plainBaseField,
    additionalContactName: plainBaseField,
    additionalContactPhone: plainBaseField,
    additionalContactEmail: plainBaseField,
  })
  .strict();

/** The parsed `baseValues`: money bases are `Money` (or null). */
export type LotPatchBaseValues = z.output<typeof LotPatchBaseValuesSchema>;

/**
 * BL-2336 — the 409 `STALE_FIELDS` copy (signed product wording, PRJ-91).
 *
 * @param count - How many edited fields have a newer value on the server.
 * @returns The sentence, singular for one field.
 */
export function staleFieldsMessage(count: number): string {
  return count === 1
    ? '1 field you edited offline has a newer value saved on the server.'
    : `${count} fields you edited offline have newer values saved on the server.`;
}

/**
 * BL-2336 — the PATCH body's replay-control keys. They describe the save, not a
 * lot field, so the role edit scope (BL-2318) never judges them: a `baseValues`
 * key must also be an edited body key, and those are scope-checked themselves.
 */
export const LOT_PATCH_REPLAY_KEYS: readonly string[] = ['baseValues', 'overwrite'];

/** BL-2336 — the 400 when `baseValues` names a field the request does not change. */
export const BASE_VALUES_UNEDITED_FIELD_MESSAGE = 'baseValues names a field this save doesn’t change.';

/**
 * BL-2348 — the refusal a PATCH carrying a whole `media` / `documents` array
 * gets. Only a tab loaded before BL-2348 deployed still sends one; reloading
 * brings the drawer that sends the per-item change lists.
 */
export const WHOLE_ATTACHMENT_ARRAY_RETIRED_MESSAGE =
  'Photos and documents now save a different way. Reload the page, then save again.';

/** A key the PATCH no longer accepts: absent is fine, any present value is refused. */
const retiredKey = z
  .custom<never>(() => false, WHOLE_ATTACHMENT_ARRAY_RETIRED_MESSAGE)
  .optional();

/**
 * The lot-edit PATCH body. Every money field is MoneyWire, decoded to `Money`:
 * - `startPrice` — nullable HERE only so the route can refuse a null in the lot's
 *   own currency copy ("Starting Price must be ¥0 or more"); the column is NOT
 *   NULL, so the route never writes one (BL-2131 D1).
 * - `reserve` — `null` is "no reserve" (BUG-1573).
 * - `increment` — `null` clears the per-lot override, so the lot follows the
 *   auction's tier table.
 * - `loadOutFee` — `null` is "no load-out fee".
 *
 * The contact slots (BL-2748: `showSalesRep`, `showSeller`, `showAdditionalContact`
 * and the four `additionalContact*` fields) are each optional, so a patch that
 * omits one leaves its column alone.
 *
 * `mediaChanges` / `documentChanges` (BL-2348) are the per-item photo and
 * document edits; a whole `media` / `documents` array is refused.
 *
 * Still `.passthrough()` — the route reads its other keys off the body (see
 * `LotCatalogPatchSchema`).
 *
 * @param options.persistedAdditionalPhone - BUG-2132: the lot's stored Additional
 *   contact phone, read server-side, so an untouched phone still saves.
 * @param options.persistedOtherContact - BL-2783: the lot's stored other contact,
 *   which the reachable rule merges the body over.
 * @returns The PATCH body schema.
 */
export function makeLotPatchRequestSchema(options: LotContactRuleOptions = {}) {
  return LotCatalogPatchSchema.extend({
    // BL-2146 (PRJ-75 sweep P3, P1-S7): `LotCatalogPatchSchema` is `.passthrough()`
    // and never declared this key, so any string reached the column unchecked
    // (a typo, a stray HTML string, whitespace) and rendered blank everywhere.
    categoryId: LotCategoryIdSchema.optional(),
    startPrice: lotMoneyWireField('startPrice').nullable().optional(),
    reserve: lotMoneyWireField('reserve').nullable().optional(),
    increment: lotMoneyWireField('increment').nullable().optional(),
    loadOutFee: lotMoneyWireField('loadOutFee').nullable().optional(),
    ...LotContactSlotFields,
    // BL-2336: the offline replay's conflict check. Absent → today's save.
    baseValues: LotPatchBaseValuesSchema.optional(),
    overwrite: z.boolean().optional(),
    // BL-2348: photos and documents change one item at a time (BL-2338's edits),
    // never as a whole array, so no caller can replace either wholesale.
    mediaChanges: LotMediaChangesSchema.optional(),
    documentChanges: LotDocumentChangesSchema.optional(),
    media: retiredKey,
    documents: retiredKey,
    // BUG-2132: a typed Additional contact phone must be a real number; the
    // stored one (read by the route before parsing) grandfathers an untouched one.
    // BL-2783: a shown other contact needs a phone or an email, judged on the
    // stored contact merged with the body.
  }).superRefine((data, ctx) => {
    addAdditionalContactPhoneIssue(data, ctx, options);
    addOtherContactReachableIssue(data, ctx, options);
  });
}

/**
 * The lot-edit PATCH body with no stored phone to grandfather against: every
 * typed Additional contact phone is judged. The route parses with
 * {@link makeLotPatchRequestSchema} and the lot's stored phone instead.
 */
export const LotPatchRequestSchema = makeLotPatchRequestSchema();

/** The parsed PATCH body: every money field is already a `Money` (or null). */
export type LotPatchRequest = z.output<typeof LotPatchRequestSchema>;
